import { i18n } from '@lingui/core'
import { en, sv, fi, de, sl, fr, sr, is } from 'make-plural/plurals'
import dayjs from 'dayjs'
import { Locale } from 'dayjs/locale/*'
import svLocale from 'dayjs/locale/sv'
import fiLocale from 'dayjs/locale/fi'
import deLocale from 'dayjs/locale/de'
import slLocale from 'dayjs/locale/sl'
import frLocale from 'dayjs/locale/fr'
import srLocale from 'dayjs/locale/sr'
import isLocale from 'dayjs/locale/is'
import { User } from '../interfaces'
import { messages } from 'locales/en/messages'

// BCP 47 Locale: https://www.unicode.org/cldr/charts/48/supplemental/language_plural_rules.html
// translationIO: https://translation.io/docs/languages
// We have abbrevations for simplicity. So if we need more specific locales later
// we will need to refactor

export const locales = [
  { translationIO: 'en', abbr: 'en', name: 'English' },
  { translationIO: 'sv', abbr: 'sv', name: 'Svenska' },
  { translationIO: 'fi', abbr: 'fi', name: 'Suomi' },
  { translationIO: 'de', abbr: 'de', name: 'Deutsch' },
  { translationIO: 'sl', abbr: 'sl', name: 'Slovenian' },
  { translationIO: 'fr', abbr: 'fr', name: 'French' },
  { translationIO: 'sr-Latn-SP', abbr: 'sr', name: 'Serbian' },
  { translationIO: 'is', abbr: 'is', name: 'Íslenska' }
]

export function activateDateSettings(locale: string) {
  dynamicActivate(locale)
  // Set locale on dayjs globally
  let preferredDayjsLocale: string | Locale = 'en'
  switch (getCurrentLocale()) {
    case 'sv':
      preferredDayjsLocale = svLocale
      break
    case 'fi':
      preferredDayjsLocale = fiLocale
      break
    case 'de':
      preferredDayjsLocale = deLocale
      break
    case 'sl':
      preferredDayjsLocale = slLocale
      break
    case 'fr':
      preferredDayjsLocale = frLocale
      break
    case 'sr':
      preferredDayjsLocale = srLocale
      break
    case 'is':
      preferredDayjsLocale = isLocale
      break
    default:
      preferredDayjsLocale = 'en'
  }

  dayjs.locale(preferredDayjsLocale)
}

const findDefaultLocale = (): string => {
  if (navigator.language) {
    // Try to use browser default
    const browserLang = navigator.language.substring(0, 2)
    if (matchLocale(browserLang)) {
      return browserLang
    }
  }
  // Fallback to 'en'
  return 'en'
}

const findPreferedLocale = (
  suggested: string | undefined = undefined
): string => {
  const sessionLocale = sessionStorage.getItem('language') || undefined
  if (sessionLocale && matchLocale(sessionLocale)) {
    return sessionLocale
  }
  if (suggested && matchLocale(suggested)) {
    return suggested
  }
  return defaultLocale
}

const findTranslationIOLocale = (locale: string): string => {
  for (const element of locales) {
    if (element.abbr === locale) {
      return element.translationIO
    }
  }

  return 'en'
}

const matchLocale = (locale: string): boolean => {
  for (const element of locales) {
    if (element.abbr === locale) {
      return true
    }
  }
  return false
}

export const defaultLocale = findDefaultLocale()

export const getCurrentLocale = (user?: User): string => {
  const sessionLocale = sessionStorage.getItem('language') || user?.locale
  if (sessionLocale && matchLocale(sessionLocale)) {
    return sessionLocale
  }
  return defaultLocale
}

export const getFullCurrentLocale = (): string => {
  switch (getCurrentLocale()) {
    case 'sv':
      return 'sv_SE'
    case 'en':
      return 'en_US'
    case 'fi':
      return 'fi_FI'
    case 'de':
      return 'de_DE'
    case 'sl':
      return 'sl_SI'
    case 'fr':
      return 'fr_FR'
    case 'sr':
      return 'sr_Latn'
    case 'is':
      return 'is_IS'
    default:
      return 'en_US'
  }
}

i18n.loadLocaleData({
  en: { plurals: en },
  sv: { plurals: sv },
  fi: { plurals: fi },
  de: { plurals: de },
  sl: { plurals: sl },
  fr: { plurals: fr },
  sr: { plurals: sr },
  is: { plurals: is }
})
// i18n needs to load "something" initially or it will complain, making things look broken.
i18n.load('en', messages)
i18n.activate('en')

/**
 * We do a dynamic import of just the catalog that we need
 * @param locale any locale string
 */
export async function dynamicActivate(suggestedLocale: string | undefined) {
  if (suggestedLocale) {
    const preferred = findPreferedLocale(suggestedLocale)
    const translationsIOLocale = findTranslationIOLocale(preferred)
    window.document.documentElement.lang = suggestedLocale
    const { messages } = await import(
      `../locales/${translationsIOLocale}/messages`
    )
    i18n.load(preferred, messages)
    i18n.activate(preferred)
    sessionStorage.setItem('language', preferred)
  }
}
